import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { passwordManagerIgnoreProps } from "@/lib/no-password-manager-props"
import { cn } from "@/lib/utils"

const inputVariants = cva(
  "w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30",
  {
    variants: {
      size: {
        default: "h-9",
        // 32px, to sit beside a Button size="sm" or SelectTrigger size="sm" (filter bars, table toolbars).
        sm: "h-8",
        // 28px, beside a Button size="compact" in dense rows and side panels.
        compact: "h-7 px-2.5",
      },
    },
    defaultVariants: { size: "default" },
  }
)

/**
 * Sign-in fields, the only ones a password manager may fill. Every other box tells password managers to
 * keep out (passwordManagerIgnoreProps): 1Password otherwise offers logins and identities in search
 * boxes, prices and notes.
 */
const SIGN_IN_AUTOCOMPLETE = new Set(["username", "email", "current-password", "new-password", "one-time-code"])

/** `size` is the height ("sm", "compact"), not the HTML attribute's character width. */
type InputProps = Omit<React.ComponentProps<"input">, "size"> & VariantProps<typeof inputVariants>

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, ...props }, ref) => {
    return (
      <input
        type={type}
        {...(type === "password" || SIGN_IN_AUTOCOMPLETE.has(String(props.autoComplete ?? "")) ? {} : passwordManagerIgnoreProps)}
        data-slot="input"
        data-size={size ?? "default"}
        className={cn(
          inputVariants({ size }),
          "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
          "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
export type { InputProps }
